import axios, { type AxiosError, type AxiosRequestConfig } from 'axios';

import type { ApiErrorDto } from './generated/model';

/**
 * The one axios instance every generated hook goes through. OpenAPI paths
 * already carry the `/api` prefix, so the default baseURL is same-origin ('').
 * Apps that call the API cross-origin set `apiClient.defaults.baseURL` at boot.
 */
export const apiClient = axios.create({ withCredentials: true });

type TokenGetter = () => Promise<string | null | undefined>;

let getAuthToken: TokenGetter | undefined;
let onUnauthorized: (() => void) | undefined;

/**
 * Where session tokens come from. Called before every request; the token goes
 * out as `Authorization: Bearer <token>`. The app registers this once at boot.
 */
export function setAuthTokenGetter(getter: TokenGetter | undefined): void {
  getAuthToken = getter;
}

/** Called when the API answers 401 - the session is gone or no longer valid. */
export function setUnauthorizedHandler(handler: (() => void) | undefined): void {
  onUnauthorized = handler;
}

apiClient.interceptors.request.use(async (config) => {
  const token = await getAuthToken?.();
  if (token) config.headers.set('Authorization', `Bearer ${token}`);
  return config;
});

apiClient.interceptors.response.use(undefined, (error: unknown) => {
  if (axios.isAxiosError(error) && error.response?.status === 401) onUnauthorized?.();
  throw error;
});

export const customInstance = async <T>(config: AxiosRequestConfig): Promise<T> => {
  const { data } = await apiClient.request<T>(config);
  return data;
};

/** Orval types every hook's `error` with this, so `error.response?.data` is ApiErrorDto. */
export type ErrorType<E = ApiErrorDto> = AxiosError<E>;

export type BodyType<B> = B;

export default customInstance;
